import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { isEmpty, isNil } from 'lodash'
import { IconPencil } from '@tabler/icons-react'

import { Button } from '@/components/buttons/Button'
import { CopyButton } from '@/components/buttons/CopyButton'
import { getSelectedAgent } from '@/reducers/voiceAgents/voiceAgents.helpers'

import { useAgentIdentifiers } from '../hooks/useAgentIdentifiers'
import { AGENT_LEVEL_NODE_ID } from '../llmHelpers'
import CallDataDeletionPanel from './CallDataDeletionPanel'
import ChangeSlugModal from './ChangeSlugModal'
import LanguageSettingsPanel from './LanguageSettingsPanel'
import LlmSettingsPanel from './LlmSettingsPanel'
import VoicePanel from './VoicePanel'

// Overview tab: the "Configure Agent Voice" panel fills the main (left)
// column; the fixed right column holds the agent-level LLM settings panel,
// the languages panel, and the agent's identity metadata.
// VoicePanel is keyed by agent so its local search/filter/phrase state resets
// when the user switches agents (the redux voices slice resets separately in
// applySelectedAgent).
const OverviewTab = () => {
  const selectedAgentId = useSelector((state) => state.voiceAgents.selectedAgentId)
  const agent = useSelector((state) => getSelectedAgent(state.voiceAgents))
  // Same gate as the list page's "Cartesia agent ID" column — provider
  // branding stays off customer screens (white-label).
  const isBaltoAdmin = useSelector((state) => state.currentUser.organizationid === 1)
  // The user's true org, which stays 1 while a Balto admin views another
  // tenant: the slug change follows the person, as AgentHeader's tools do.
  const canChangeSlug = useSelector((state) => state.currentUser.own_organization_id === 1)
  const [changeSlugOpen, setChangeSlugOpen] = useState(false)
  const { organizationName, voiceAgentId, cartesiaAgentId, slug } = useAgentIdentifiers()

  // Rows render only when their value is known — slug/Cartesia id resolve
  // from the provisioning record (by-user route), org name from the
  // organizations store.
  const metaRows = [
    { label: 'Organization', value: organizationName, copyable: false },
    { label: 'Agent ID', value: agent?.id, copyable: true },
    { label: 'Cartesia agent ID', value: isBaltoAdmin ? cartesiaAgentId : null, copyable: true },
    { label: 'Slug', value: slug, copyable: true, editable: canChangeSlug },
  ].filter((row) => Boolean(row.value))

  return (
    <div className="voice-agents__overview" data-testid="overview-tab">
      <VoicePanel key={selectedAgentId} />
      <section className="voice-agents__overview-side">
        {!isEmpty(metaRows) && (
          <div className="voice-agents__overview-meta" data-testid="overview-metadata">
            <h3 className="voice-agents__overview-meta-title">Metadata</h3>
            {metaRows.map((row) => (
              <div key={row.label} className="voice-agents__overview-meta-row">
                <span className="voice-agents__overview-meta-label">{row.label}</span>
                <span className="voice-agents__overview-meta-value">
                  {row.value}
                  {row.editable && (
                    <Button
                      basic
                      small
                      square
                      icon={<IconPencil />}
                      tooltip="Change slug"
                      aria-label="Change slug"
                      onClick={() => setChangeSlugOpen(true)}
                      data-testid="overview-change-slug"
                    />
                  )}
                  {row.copyable && (
                    <CopyButton
                      content={row.value}
                      iconOnly
                      showTooltip
                      tooltipContent={`Copy ${row.label.toLowerCase()}`}
                    />
                  )}
                </span>
              </div>
            ))}
          </div>
        )}
        <LlmSettingsPanel key={`llm-${selectedAgentId}`} nodeId={AGENT_LEVEL_NODE_ID} />
        <LanguageSettingsPanel key={`languages-${selectedAgentId}`} />
        {/* Hidden without a resolved voice_agents row — the legacy
            /voice-agents/:agentId route has no provisioning record to key the
            flag by, same as the Cartesia agent ID row above. */}
        {isBaltoAdmin && !isNil(voiceAgentId) && (
          <CallDataDeletionPanel voiceAgentId={voiceAgentId} />
        )}
      </section>
      {canChangeSlug && (
        <ChangeSlugModal
          show={changeSlugOpen}
          onClose={() => setChangeSlugOpen(false)}
          voiceAgentId={voiceAgentId}
          currentSlug={slug}
        />
      )}
    </div>
  )
}

export default OverviewTab
